// Toast — minimal in-app notification system. Replaces window.alert /
// window.confirm scattered across the codebase.
//
// Decision (Session 19AT · S2):
//   - No third-party dep (sonner / react-hot-toast). ~100 lines hand-rolled,
//     fits ExamForge's "no SaaS bloat" stance + zero install cost.
//   - 4 levels: success / info / warn / error. error is sticky (no auto-dismiss);
//     others auto-dismiss after 4s.
//   - Stacks bottom-right, max 5 visible (oldest dropped).
//   - Keyboard: Esc dismisses focused toast.
//
// Usage:
//   const toast = useToast();
//   toast.success('已保存');
//   toast.error('IDB 不可用 · 进度本会话不保存', { sticky: true });

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { Check, Info, AlertTriangle, AlertOctagon, X } from 'lucide-react';
import { useT } from '../i18n/use-t.js';

export type ToastLevel = 'success' | 'info' | 'warn' | 'error';

export interface ToastOptions {
  /** Don't auto-dismiss. Default: true for 'error', false otherwise. */
  sticky?: boolean;
  /** Custom action button + handler. */
  action?: { label: string; onClick: () => void };
}

interface ToastEntry {
  id: number;
  level: ToastLevel;
  message: string;
  sticky: boolean;
  action?: { label: string; onClick: () => void };
  createdAt: number;
}

interface ToastContext {
  show(level: ToastLevel, message: string, opts?: ToastOptions): number;
  success(message: string, opts?: ToastOptions): number;
  info(message: string, opts?: ToastOptions): number;
  warn(message: string, opts?: ToastOptions): number;
  error(message: string, opts?: ToastOptions): number;
  dismiss(id: number): void;
}

const Ctx = createContext<ToastContext | null>(null);

const AUTO_DISMISS_MS = 4000;
const MAX_VISIBLE = 5;

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  // Track timeout handles so dismiss() can cancel auto-dismiss.
  const timeoutsRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    const tm = timeoutsRef.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const show = useCallback((level: ToastLevel, message: string, opts: ToastOptions = {}): number => {
    const id = nextId++;
    const sticky = opts.sticky ?? (level === 'error');
    const entry: ToastEntry = {
      id, level, message, sticky,
      ...(opts.action ? { action: opts.action } : {}),
      createdAt: Date.now(),
    };
    setEntries((prev) => {
      const next = [...prev, entry];
      // Cap visible — drop oldest non-sticky when over.
      if (next.length > MAX_VISIBLE) {
        const droppable = next.findIndex((e) => !e.sticky);
        if (droppable >= 0) next.splice(droppable, 1);
        else next.shift();
      }
      return next;
    });
    if (!sticky) {
      const tm = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      timeoutsRef.current.set(id, tm);
    }
    return id;
  }, [dismiss]);

  const ctx = useMemo<ToastContext>(() => ({
    show,
    success: (m, o) => show('success', m, o),
    info:    (m, o) => show('info',    m, o),
    warn:    (m, o) => show('warn',    m, o),
    error:   (m, o) => show('error',   m, o),
    dismiss,
  }), [show, dismiss]);

  // Cleanup on unmount.
  useEffect(() => {
    const map = timeoutsRef.current;
    return () => {
      for (const tm of map.values()) clearTimeout(tm);
      map.clear();
    };
  }, []);

  return (
    <Ctx.Provider value={ctx}>
      {children}
      <ToastViewport entries={entries} onDismiss={dismiss} />
    </Ctx.Provider>
  );
}

export function useToast(): ToastContext {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Defensive: never throw in render path. Return a no-op proxy that logs
    // to console — better than crashing if a stray useToast escapes Provider.
    // eslint-disable-next-line no-console
    console.warn('[useToast] called outside ToastProvider; returning no-op');
    const noop = (): number => 0;
    return {
      show: noop, success: noop, info: noop, warn: noop, error: noop,
      dismiss: () => undefined,
    };
  }
  return ctx;
}

function ToastViewport({ entries, onDismiss }: { entries: ToastEntry[]; onDismiss: (id: number) => void }) {
  return (
    <div className="toast-viewport" role="status" aria-live="polite">
      {entries.map((e) => (
        <ToastItem key={e.id} entry={e} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ entry, onDismiss }: { entry: ToastEntry; onDismiss: (id: number) => void }) {
  const t = useT();
  const Icon = ICONS[entry.level];
  return (
    <div className={`toast toast-${entry.level}`} role="alert">
      <Icon size={14} aria-hidden="true" className="toast-icon" />
      <span className="toast-msg">{entry.message}</span>
      {entry.action && (
        <button
          type="button"
          className="toast-action-btn"
          onClick={() => {
            entry.action?.onClick();
            onDismiss(entry.id);
          }}
        >
          {entry.action.label}
        </button>
      )}
      <button
        type="button"
        className="toast-close"
        aria-label={t('common.close')}
        onClick={() => onDismiss(entry.id)}
      >
        <X size={11} aria-hidden="true" />
      </button>
    </div>
  );
}

const ICONS = {
  success: Check,
  info: Info,
  warn: AlertTriangle,
  error: AlertOctagon,
} as const;
